'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    getJournalEntries,
    reviewJournalEntries,
    deleteJournalEntry,
    exportLivroDiario,
    JournalEntry,
    JournalEntryStatus,
    JournalEntryOrigin,
} from '@/lib/api/financial/financial-accounting-api'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCheckCircle,
    MdDelete,
    MdFilterList,
    MdPlaylistAddCheck,
    MdReceipt,
    MdSmartToy,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<JournalEntryStatus, string> = {
    PENDING: 'Pendente',
    POSTED: 'Lançado',
    REVIEWED: 'Revisado',
}

const STATUS_BADGE: Record<JournalEntryStatus, string> = {
    PENDING: 'badge-warning',
    POSTED: 'badge-info',
    REVIEWED: 'badge-success',
}

const ORIGIN_LABELS: Record<JournalEntryOrigin, string> = {
    AUTOMATIC: 'Automático',
    AI_CLASSIFIED: 'IA',
    MANUAL: 'Manual',
}

const ORIGIN_BADGE: Record<JournalEntryOrigin, string> = {
    AUTOMATIC: 'badge-secondary',
    AI_CLASSIFIED: 'badge-secondary',
    MANUAL: 'badge-ghost',
}

const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export default function JournalEntriesPage() {
    const navigate = useNavigate()
    const [entries, setEntries] = useState<JournalEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    // Filtros
    const [statusFilter, setStatusFilter] = useState<JournalEntryStatus | ''>('')
    const [originFilter, setOriginFilter] = useState<JournalEntryOrigin | ''>('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    // Seleção
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Review
    const [reviewing, setReviewing] = useState(false)

    useEffect(() => {
        loadData()
    }, [page, statusFilter, originFilter, startDate, endDate])

    const loadData = async () => {
        setLoading(true)
        try {
            const response = await getJournalEntries({
                status: statusFilter || undefined,
                origin: originFilter || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                page,
                limit: 30,
            })
            if (response.success && response.result) {
                setEntries(response.result.items)
                setTotal(response.result.total)
                setTotalPages(response.result.totalPages)
            }
        } catch {
            toast.error('Erro ao carregar lançamentos')
        } finally {
            setLoading(false)
        }
    }

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleSelectAll = () => {
        if (selectedIds.size === entries.length) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(entries.map(e => e.id)))
        }
    }

    const handleReview = async () => {
        if (selectedIds.size === 0) return
        setReviewing(true)
        try {
            const response = await reviewJournalEntries(Array.from(selectedIds))
            if (response.success) {
                toast.success(`${response.result?.reviewed || 0} lançamento(s) revisado(s)`)
                setSelectedIds(new Set())
                loadData()
            } else {
                toast.error(response.error || 'Erro ao revisar')
            }
        } catch {
            toast.error('Erro ao revisar lançamentos')
        } finally {
            setReviewing(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const response = await deleteJournalEntry(deleteId)
            if (response.success) {
                toast.success('Lançamento excluído')
                setDeleteId(null)
                loadData()
            } else {
                toast.error(response.error || 'Erro ao excluir')
            }
        } catch {
            toast.error('Erro ao excluir lançamento')
        } finally {
            setDeleting(false)
        }
    }

    const unreviewedSelected = entries.filter(
        e => selectedIds.has(e.id) && e.status !== 'REVIEWED' && e.lines.length > 0
    ).length

    const unclassifiedSelected = entries.filter(
        e => selectedIds.has(e.id) && e.lines.length === 0
    ).length

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Lançamentos Contábeis</h1>
                        <p className="text-base-content/60 text-sm mt-1">
                            Partidas dobradas - {total} lançamento{total !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {unclassifiedSelected > 0 && (
                        <span className="text-xs text-warning">
                            {unclassifiedSelected} sem classificação
                        </span>
                    )}
                    {unreviewedSelected > 0 && (
                        <button
                            className="btn btn-success btn-sm gap-1"
                            onClick={handleReview}
                            disabled={reviewing}
                        >
                            {reviewing ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <MdPlaylistAddCheck size={18} />
                            )}
                            Revisar ({unreviewedSelected})
                        </button>
                    )}
                    <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                            Exportar
                        </label>
                        <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-50 w-48 p-2 shadow-lg border border-base-300">
                            <li><button onClick={() => {
                                const s = startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)
                                const e = endDate || new Date().toISOString().slice(0, 10)
                                exportLivroDiario(s, e, 'xlsx').catch(() => toast.error('Erro ao exportar'))
                            }}>Livro Diário (Excel)</button></li>
                            <li><button onClick={() => {
                                const s = startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)
                                const e = endDate || new Date().toISOString().slice(0, 10)
                                exportLivroDiario(s, e, 'pdf').catch(() => toast.error('Erro ao exportar'))
                            }}>Livro Diário (PDF)</button></li>
                        </ul>
                    </div>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/financial/accounting-rules')}
                    >
                        <MdFilterList size={18} />
                        Regras
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                    className="select select-bordered select-sm"
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value as JournalEntryStatus | ''); setPage(1) }}
                >
                    <option value="">Todos os status</option>
                    <option value="PENDING">Pendente</option>
                    <option value="POSTED">Lançado</option>
                    <option value="REVIEWED">Revisado</option>
                </select>

                <select
                    className="select select-bordered select-sm"
                    value={originFilter}
                    onChange={e => { setOriginFilter(e.target.value as JournalEntryOrigin | ''); setPage(1) }}
                >
                    <option value="">Todas as origens</option>
                    <option value="AUTOMATIC">Automático</option>
                    <option value="AI_CLASSIFIED">IA</option>
                    <option value="MANUAL">Manual</option>
                </select>

                <input
                    type="date"
                    className="input input-bordered input-sm"
                    value={startDate}
                    onChange={e => { setStartDate(e.target.value); setPage(1) }}
                    placeholder="Data início"
                />
                <input
                    type="date"
                    className="input input-bordered input-sm"
                    value={endDate}
                    onChange={e => { setEndDate(e.target.value); setPage(1) }}
                    placeholder="Data fim"
                />

                {(statusFilter || originFilter || startDate || endDate) && (
                    <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => {
                            setStatusFilter('')
                            setOriginFilter('')
                            setStartDate('')
                            setEndDate('')
                            setPage(1)
                        }}
                    >
                        Limpar filtros
                    </button>
                )}
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {/* Empty */}
            {!loading && entries.length === 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdReceipt size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhum lançamento encontrado</p>
                    </div>
                </div>
            )}

            {/* Selecionar todos */}
            {!loading && entries.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-xs"
                            checked={selectedIds.size === entries.length && entries.length > 0}
                            onChange={toggleSelectAll}
                        />
                        <span className="text-xs text-base-content/60">Selecionar todos</span>
                    </label>
                </div>
            )}

            {/* Cards */}
            {!loading && entries.length > 0 && (
                <div className="flex flex-col gap-3">
                    {entries.map(entry => {
                        const debitLines = entry.lines.filter(l => l.type === 'DEBIT')
                        const creditLines = entry.lines.filter(l => l.type === 'CREDIT')
                        const totalAmount = debitLines.reduce((sum, l) => sum + l.amount, 0)
                        const displayAmount = totalAmount > 0 ? totalAmount : (entry.billing?.amount || 0)
                        const hasLines = entry.lines.length > 0

                        return (
                            <div
                                key={entry.id}
                                className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                                onClick={() => navigate(`/financial/journal-entries/${entry.id}`)}
                            >
                                <div className="flex items-start gap-4 p-4">
                                    {/* Checkbox + Ícone */}
                                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                                        <div onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={selectedIds.has(entry.id)}
                                                onChange={() => toggleSelect(entry.id)}
                                            />
                                        </div>
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            !hasLines
                                                ? 'bg-warning/20 border border-warning/30'
                                                : entry.billing?.type === 'PAYABLE'
                                                    ? 'bg-error/10 border border-error/20'
                                                    : 'bg-success/10 border border-success/20'
                                        }`}>
                                            <MdReceipt className={`text-xl ${
                                                !hasLines
                                                    ? 'text-warning'
                                                    : entry.billing?.type === 'PAYABLE'
                                                        ? 'text-error'
                                                        : 'text-success'
                                            }`} />
                                        </div>
                                    </div>

                                    {/* Conteúdo principal */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <div className="min-w-0">
                                                <h3 className="text-sm font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                    {entry.description}
                                                </h3>
                                                {entry.billing && (
                                                    <p className="text-xs text-base-content/50 mt-0.5">
                                                        Cobrança #{entry.billingId}
                                                        {entry.billing.client && ` - ${entry.billing.client.name}`}
                                                        {entry.billing.supplier && ` - ${entry.billing.supplier.tradingName}`}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Valor */}
                                            <div className="text-right flex-shrink-0">
                                                <p className={`text-base font-bold ${
                                                    !hasLines
                                                        ? 'text-base-content/50'
                                                        : entry.billing?.type === 'PAYABLE'
                                                            ? 'text-error'
                                                            : 'text-success'
                                                }`}>
                                                    {formatCurrency(displayAmount)}
                                                </p>
                                                <p className="text-[10px] text-base-content/40 font-mono">
                                                    {formatDate(entry.date)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Contas débito/crédito */}
                                        {hasLines ? (
                                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-2">
                                                {debitLines.map(l => (
                                                    <span key={l.id} className="text-xs">
                                                        <span className="text-error font-medium">D:</span>{' '}
                                                        <span className="text-base-content/70">{l.account.code} {l.account.title}</span>
                                                    </span>
                                                ))}
                                                {creditLines.map(l => (
                                                    <span key={l.id} className="text-xs">
                                                        <span className="text-success font-medium">C:</span>{' '}
                                                        <span className="text-base-content/70">{l.account.code} {l.account.title}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-warning mt-2">
                                                Pendente de classificação no plano de contas
                                            </p>
                                        )}

                                        {/* Badges + Ações */}
                                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-base-300/30">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`badge badge-sm ${STATUS_BADGE[entry.status]}`}>
                                                    {STATUS_LABELS[entry.status]}
                                                </span>
                                                <span className={`badge badge-sm ${ORIGIN_BADGE[entry.origin]}`}>
                                                    {entry.origin === 'AI_CLASSIFIED' && <MdSmartToy size={10} className="mr-1" />}
                                                    {ORIGIN_LABELS[entry.origin]}
                                                </span>
                                                {entry.aiConfidence !== null && (
                                                    <span className="text-[10px] text-base-content/40">
                                                        {Math.round(entry.aiConfidence * 100)}% confiança
                                                    </span>
                                                )}
                                                {entry.accountingRule && (
                                                    <span className="text-[10px] text-base-content/40">
                                                        Regra: {entry.accountingRule.name}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                {entry.status !== 'REVIEWED' && hasLines && (
                                                    <button
                                                        className="btn btn-circle btn-sm btn-ghost text-success"
                                                        onClick={() => {
                                                            setSelectedIds(new Set([entry.id]))
                                                            handleReview()
                                                        }}
                                                        title="Revisar"
                                                    >
                                                        <MdCheckCircle size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-circle btn-sm btn-ghost text-error"
                                                    onClick={() => setDeleteId(entry.id)}
                                                    title="Excluir"
                                                >
                                                    <MdDelete size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                    <div className="join">
                        <button
                            className="join-item btn btn-sm"
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            Anterior
                        </button>
                        <button className="join-item btn btn-sm btn-disabled">
                            {page} / {totalPages}
                        </button>
                        <button
                            className="join-item btn btn-sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Lançamento"
                message="Tem certeza de que deseja excluir este lançamento contábil?"
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
